import React, { useState, useEffect, useRef } from 'react';
import {
  Save, Send, Loader, CalendarClock, Users as UsersIcon,
  Download, CheckCircle, XCircle, AlertCircle, Upload, FileSpreadsheet, X, Image,
} from 'lucide-react';
import { attendanceAPI } from '../api/apiService';
import { useDropdownData } from '../utils/useDropdownData';
import { useAuth, useActiveEstId } from './AuthContext';
import * as XLSX from 'xlsx';
import EstablishmentCard from './EstablishmentCard';
import './ShiftTracker.css';

const ShiftTracker = () => {
  const { user } = useAuth();
  const lockedEstId = useActiveEstId();

  const currentDate = new Date();
  const currentMonth = currentDate.getMonth() + 1;
  const currentYear = currentDate.getFullYear();

  const [selectedEstablishment, setSelectedEstablishment] = useState(lockedEstId || '');
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [employeeShifts, setEmployeeShifts] = useState({});
  const [openLeaveDropdown, setOpenLeaveDropdown] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [holidays, setHolidays] = useState([]);
  const [toast, setToast] = useState(null);
  const [activeTab, setActiveTab] = useState('grid');
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [parsedShifts, setParsedShifts] = useState(null);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const fileInputRef = useRef(null);

  const {
    establishments, employees, loading,
    fetchEstablishments, fetchEmployeesByEstablishment, clearEmployees,
  } = useDropdownData();

  useEffect(() => {
    if (lockedEstId) {
      setSelectedEstablishment(lockedEstId);
      fetchEmployeesByEstablishment(lockedEstId);
    }
  }, [lockedEstId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (lockedEstId) {
      fetchEmployeesByEstablishment(lockedEstId);
    } else {
      fetchEstablishments();
    }
  }, [fetchEstablishments]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (lockedEstId) return;
    if (establishments.length > 0 && !selectedEstablishment) {
      const firstEstId = establishments[0].id.toString();
      setSelectedEstablishment(firstEstId);
      fetchEmployeesByEstablishment(firstEstId);
    }
  }, [establishments]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const fetchHols = async () => {
      try {
        const response = await attendanceAPI.getHolidays(selectedYear);
        const h = (response?.holidays || response || []).map(hol => ({
          id: hol.id,
          name: hol.holiday_name || hol.holidayName,
          date: hol.holiday_date || hol.holidayDate,
          type: hol.holiday_type || hol.holidayType || 'public',
          optional: hol.optional || false,
        }));
        setHolidays(h);
      } catch { setHolidays([]); }
    };
    fetchHols();
  }, [selectedYear]);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  const shiftTypes = [
    { value: '',              label: 'Select Shift',  hours: '',            color: '' },
    { value: 'morning',      label: 'Morning',        hours: '6:00 AM – 2:00 PM',  color: '#dbeafe' },
    { value: 'day',          label: 'Day',            hours: '9:00 AM – 5:00 PM',  color: '#fef3c7' },
    { value: 'evening',      label: 'Evening',        hours: '2:00 PM – 10:00 PM', color: '#fed7aa' },
    { value: 'night',        label: 'Night',          hours: '10:00 PM – 6:00 AM', color: '#e0e7ff' },
    { value: 'planned-leave',label: 'Planned Leave',  hours: 'On Leave',           color: '#fce7f3' },
  ];

  const shiftStyle = {
    morning:        { bg: '#dbeafe', color: '#1e40af', label: 'Morning',  sub: '6AM–2PM'   },
    day:            { bg: '#fef3c7', color: '#92400e', label: 'Day',      sub: '9AM–5PM'   },
    evening:        { bg: '#fed7aa', color: '#9a3412', label: 'Evening',  sub: '2PM–10PM'  },
    night:          { bg: '#e0e7ff', color: '#3730a3', label: 'Night',    sub: '10PM–6AM'  },
    'planned-leave':{ bg: '#fce7f3', color: '#9d174d', label: 'Leave',   sub: 'On Leave'   },
  };

  const monthNames = ['January','February','March','April','May','June','July','August','September','October','November','December'];
  const dayNames   = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];

  const getMonthYearOptions = () => {
    const options = [];
    let y = currentYear, m = currentMonth - 6;
    if (m < 1) { const s = Math.abs(m) + 1; y -= Math.ceil(s / 12); m = 12 - (s % 12); }
    for (let i = 0; i < 30; i++) {
      options.push({ value: `${y}-${m}`, label: `${monthNames[m - 1]} ${y}`, month: m, year: y });
      m++; if (m > 12) { m = 1; y++; }
    }
    return options;
  };
  const monthYearOptions = getMonthYearOptions();

  const getWeeksInMonth = () => {
    const firstDay = new Date(selectedYear, selectedMonth - 1, 1);
    const lastDay  = new Date(selectedYear, selectedMonth, 0);
    const weeks = [];
    let ws = new Date(firstDay);
    for (let wn = 0; wn < 4; wn++) {
      const we = new Date(ws); we.setDate(we.getDate() + 6);
      const de = we > lastDay ? lastDay : we;
      weeks.push({ weekNum: wn + 1, startDate: new Date(ws), endDate: de, days: generateDaysForWeek(ws) });
      ws.setDate(ws.getDate() + 7);
    }
    return weeks;
  };

  const isHoliday = (date) => {
    const ds = date.toISOString().split('T')[0];
    return holidays.find(h => h.date === ds);
  };

  const generateDaysForWeek = (weekStart) => {
    const days = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(weekStart); d.setDate(d.getDate() + i);
      const h = isHoliday(d);
      days.push({ date: d, dayName: dayNames[d.getDay()], dayOfMonth: d.getDate(), value: i, label: `${dayNames[d.getDay()]}, ${monthNames[d.getMonth()]} ${d.getDate()}`, holiday: h, isHoliday: !!h });
    }
    return days;
  };

  const formatDateRange = (s, e) => {
    const sm = monthNames[s.getMonth()], em = monthNames[e.getMonth()];
    return sm === em
      ? `${sm} ${s.getDate()}–${e.getDate()}`
      : `${sm} ${s.getDate()} – ${em} ${e.getDate()}`;
  };

  const handleEstablishmentChange = (estId) => {
    setSelectedEstablishment(estId);
    setEmployeeShifts({});
    clearEmployees();
    if (estId) fetchEmployeesByEstablishment(estId);
  };

  const [shiftLoadKey, setShiftLoadKey] = useState(0);
  const reloadShifts = () => setShiftLoadKey(k => k + 1);

  useEffect(() => {
    const load = async () => {
      if (employees.length > 0 && selectedEstablishment) {
        try {
          const params = { establishment_id: selectedEstablishment, month: selectedMonth.toString(), year: selectedYear };
          const response = await attendanceAPI.getShiftSchedules(params);
          const schedules = response?.shifts || [];
          const sm = {};
          employees.forEach(emp => {
            const eid = emp.employee_id || emp.employeeId;
            if (eid) sm[eid] = {
              week1: { shiftType: '', offDay: '', leaveDays: [], shiftHours: '' },
              week2: { shiftType: '', offDay: '', leaveDays: [], shiftHours: '' },
              week3: { shiftType: '', offDay: '', leaveDays: [], shiftHours: '' },
              week4: { shiftType: '', offDay: '', leaveDays: [], shiftHours: '' },
            };
          });
          schedules.forEach(s => {
            const eid = s.employee_id || s.employeeId;
            const wn  = s.week_number || s.weekNumber;
            const st  = s.shift_type  || s.shiftType;
            const od  = s.off_day ?? s.offDay ?? '';
            const ld  = s.leave_days  || s.leaveDays;
            const wk  = `week${wn}`;
            if (sm[eid]) {
              const std = shiftTypes.find(x => x.value === st);
              sm[eid][wk] = { shiftType: st || '', offDay: od, leaveDays: ld ? ld.split(',').map(Number) : [], shiftHours: std?.hours || '', color: std?.color || '' };
            }
          });
          setEmployeeShifts(sm);
        } catch {
          const init = {};
          employees.forEach(emp => {
            const eid = emp.employee_id || emp.employeeId;
            if (eid) init[eid] = {
              week1: { shiftType: '', offDay: '', leaveDays: [], shiftHours: '' },
              week2: { shiftType: '', offDay: '', leaveDays: [], shiftHours: '' },
              week3: { shiftType: '', offDay: '', leaveDays: [], shiftHours: '' },
              week4: { shiftType: '', offDay: '', leaveDays: [], shiftHours: '' },
            };
          });
          setEmployeeShifts(init);
        }
      }
    };
    load();
  }, [employees, selectedEstablishment, selectedMonth, selectedYear, shiftLoadKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const onClick = (e) => {
      if (openLeaveDropdown && !e.target.closest('[data-shift-popover]')) setOpenLeaveDropdown(null);
    };
    if (openLeaveDropdown) {
      document.addEventListener('mousedown', onClick);
      return () => document.removeEventListener('mousedown', onClick);
    }
  }, [openLeaveDropdown]);

  const handleMonthYearChange = (v) => {
    const [y, m] = v.split('-');
    setSelectedYear(parseInt(y));
    setSelectedMonth(parseInt(m));
  };

  const handleShiftChange = (empId, week, st) => {
    const s = shiftTypes.find(x => x.value === st);
    setEmployeeShifts(p => ({ ...p, [empId]: { ...p[empId], [week]: { ...p[empId][week], shiftType: st, shiftHours: s?.hours || '', color: s?.color || '' } } }));
  };

  const handleOffDayChange = (empId, week, idx) => {
    setEmployeeShifts(p => ({ ...p, [empId]: { ...p[empId], [week]: { ...p[empId][week], offDay: idx } } }));
  };

  const getEmployees = () => employees.map(emp => {
    const eid = emp.employee_id || emp.employeeId;
    const fn  = emp.first_name  || emp.firstName  || '';
    const ln  = emp.last_name   || emp.lastName   || '';
    return { id: eid, name: `${fn} ${ln}`.trim() || eid, empId: eid, department: emp.department || 'N/A' };
  });

  // ── Upload Handlers ──────────────────────────────────────────────────────
  const handleFileDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) { const ext = file.name.split('.').pop().toLowerCase(); if (['xlsx','xls','csv','jpg','jpeg','png','webp','bmp','pdf'].includes(ext)) setUploadFile(file); }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) { const ext = file.name.split('.').pop().toLowerCase(); if (['xlsx','xls','csv','jpg','jpeg','png','webp','bmp','pdf'].includes(ext)) setUploadFile(file); }
  };

  const getFileType = (file) => {
    if (!file) return null;
    const ext = file.name.split('.').pop().toLowerCase();
    if (['xlsx','xls','csv'].includes(ext)) return 'spreadsheet';
    if (['jpg','jpeg','png','webp','bmp','pdf'].includes(ext)) return 'image';
    return null;
  };

  const handleParseUpload = async () => {
    if (!uploadFile || !selectedEstablishment) return;
    setUploading(true); setParsedShifts(null);
    try {
      const fileType = getFileType(uploadFile);
      let result;
      if (fileType === 'image') {
        result = await attendanceAPI.uploadShiftImage(uploadFile, selectedEstablishment, selectedMonth, selectedYear);
        const records = (result.records || []).map(r => ({
          employee_id:   r.employee_id,
          employee_name: r.employee_name || r.employee_id,
          week_number:   r.week_number || 1,
          shift_type:    r.shift_type || 'day',
          off_day:       r.off_day ?? null,
        }));
        setParsedShifts({ records, unmatched: result.unmatched_employees || [], week_info: result.week_info || [] });
      } else {
        result = await attendanceAPI.uploadShiftSpreadsheet(uploadFile, selectedEstablishment, selectedMonth, selectedYear);
        setParsedShifts(result);
      }
      if (result.records?.length) showToast(`Parsed ${result.records.length} shift records — review and submit`);
      else showToast('No shift records found in file', 'error');
    } catch (err) {
      showToast('Failed to parse: ' + (err.message || 'Unknown error'), 'error');
    } finally {
      setUploading(false);
    }
  };

  const handleConfirmUpload = async () => {
    if (!parsedShifts?.records?.length) return;
    setConfirmLoading(true);
    try {
      const result = await attendanceAPI.confirmShiftSpreadsheet(parsedShifts.records, selectedEstablishment, selectedMonth, selectedYear);
      showToast(`Successfully saved ${result?.count || parsedShifts.records.length} shift records`);
      setParsedShifts(null); setUploadFile(null); setActiveTab('grid'); reloadShifts();
    } catch (err) {
      showToast('Failed to save: ' + (err.message || 'Unknown error'), 'error');
    } finally {
      setConfirmLoading(false);
    }
  };

  const removeShiftRecord = (idx) => {
    setParsedShifts(prev => ({ ...prev, records: prev.records.filter((_, i) => i !== idx) }));
  };

  const handleDownloadTemplate = async () => {
    try {
      await attendanceAPI.downloadShiftTemplate(selectedEstablishment, selectedMonth, selectedYear);
      showToast('Template downloaded');
    } catch (err) {
      showToast('Failed to download template: ' + err.message, 'error');
    }
  };

  const handleSaveToExcel = () => {
    const emps = getEmployees();
    if (!emps.length) return;
    const rows = [];
    const header = ['Employee ID', 'Employee Name', 'Department'];
    weeks.forEach((_, i) => { header.push(`Week ${i+1} - Shift`, `Week ${i+1} - Hours`, `Week ${i+1} - Off Day`, `Week ${i+1} - Leave Days`); });
    rows.push(header);
    emps.forEach(emp => {
      const r = [emp.empId, emp.name, emp.department];
      weeks.forEach((w, wi) => {
        const wd = employeeShifts[emp.id]?.[`week${wi+1}`];
        if (wd?.shiftType) {
          r.push(shiftTypes.find(s => s.value === wd.shiftType)?.label || '', wd.shiftHours || '');
          r.push(wd.offDay !== '' && wd.offDay !== undefined ? `${w.days[wd.offDay]?.dayName}, ${w.days[wd.offDay]?.dayOfMonth}` : '');
          r.push(wd.leaveDays?.length ? wd.leaveDays.map(di => `${w.days[di]?.dayName} ${w.days[di]?.dayOfMonth}`).join('; ') : '');
        } else r.push('', '', '', '');
      });
      rows.push(r);
    });
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(rows);
    XLSX.utils.book_append_sheet(wb, ws, 'Shift Schedule');
    XLSX.writeFile(wb, `Shift_Schedule_${monthNames[selectedMonth-1]}_${selectedYear}.xlsx`);
  };

  const handleSubmit = async () => {
    const emps = getEmployees();
    if (!emps.length) return;
    setIsSubmitting(true);
    try {
      const recs = [];
      emps.forEach(emp => {
        weeks.forEach((_, wi) => {
          const wk = `week${wi+1}`;
          const wd = employeeShifts[emp.id]?.[wk];
          if (wd?.shiftType) {
            recs.push({
              establishmentId: parseInt(selectedEstablishment) || 1,
              employeeId: emp.empId, month: selectedMonth.toString(), year: selectedYear,
              weekNumber: wi + 1, shiftType: wd.shiftType,
              offDay: wd.offDay !== '' && wd.offDay !== undefined ? wd.offDay.toString() : null,
              leaveDays: wd.leaveDays?.length ? wd.leaveDays.join(',') : null,
              shiftHours: 8,
            });
          }
        });
      });
      if (!recs.length) { setIsSubmitting(false); showToast('Please assign shifts to at least one employee', 'error'); return; }
      await Promise.all(recs.map(r => attendanceAPI.updateShiftSchedule(r)));
      showToast(`Shift schedules submitted successfully! (${recs.length} records)`);
      reloadShifts();
    } catch (e) {
      showToast('Error saving: ' + e.message, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const weeks = getWeeksInMonth();
  const getMonthName = () => `${monthNames[selectedMonth - 1]} ${selectedYear}`;

  return (
    <div className="st-page">
      {/* Toast */}
      {toast && (
        <div className={`st-toast st-toast-${toast.type}`}>
          {toast.type === 'success' ? <CheckCircle size={15} /> : toast.type === 'error' ? <XCircle size={15} /> : <AlertCircle size={15} />}
          <span>{toast.msg}</span>
        </div>
      )}

      {/* ── Header ─────────────────────────────────────────────────────── */}
      <div className="st-header">
        <div className="st-header-icon">
          <CalendarClock size={20} />
        </div>
        <div>
          <h1 className="st-title">Shift Tracker</h1>
          <p className="st-subtitle">Manage weekly shift schedules for {getMonthName()}</p>
        </div>
      </div>

      {/* Establishment Selector */}
      {!lockedEstId && (
        <EstablishmentCard
          establishments={establishments}
          value={selectedEstablishment}
          onChange={(e) => handleEstablishmentChange(e.target.value)}
          loading={loading.establishments}
        />
      )}

      {selectedEstablishment && (
        <>
          {/* ── Period selector ─────────────────────────────────────────── */}
          <div className="st-controls">
            <div className="st-month-select">
              <label className="st-label">Period</label>
              <select
                value={`${selectedYear}-${selectedMonth}`}
                onChange={(e) => handleMonthYearChange(e.target.value)}
                className="st-select"
              >
                {monthYearOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
          </div>

          {/* ── Tabs ────────────────────────────────────────────────────── */}
          <div className="st-tabs">
            <button className={`st-tab ${activeTab === 'grid' ? 'st-tab-active' : ''}`} onClick={() => setActiveTab('grid')}>
              <CalendarClock size={15} /> Shift Grid
            </button>
            <button className={`st-tab ${activeTab === 'upload' ? 'st-tab-active' : ''}`} onClick={() => setActiveTab('upload')}>
              <Upload size={15} /> Upload Schedule
            </button>
          </div>

          {/* ── Upload Tab ──────────────────────────────────────────────── */}
          {activeTab === 'upload' && (
            <div className="st-card">
              <div className="st-card-header">
                <h2 className="st-card-title"><Upload size={17} /> Upload Shift Schedule</h2>
                <p className="st-hint">
                  Upload a spreadsheet (.xlsx, .csv), image, or PDF with shift assignments for {getMonthName()}
                </p>
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <button className="st-btn-secondary" onClick={handleDownloadTemplate} style={{ fontSize: '0.8rem' }}>
                  <Download size={13} /> Download Template
                </button>
              </div>

              {/* Dropzone */}
              <div
                className={`st-dropzone${dragOver ? ' st-dropzone-active' : ''}${uploadFile ? ' st-dropzone-has-file' : ''}`}
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleFileDrop}
                onClick={() => fileInputRef.current?.click()}
              >
                <input ref={fileInputRef} type="file" accept=".xlsx,.xls" onChange={handleFileSelect} style={{ display: 'none' }} />
                {uploadFile ? (
                  <div className="st-dropzone-file">
                    {getFileType(uploadFile) === 'image'
                      ? <Image size={30} style={{ color: 'var(--theme-primary, #2563eb)', flexShrink: 0 }} />
                      : <FileSpreadsheet size={30} style={{ color: '#059669', flexShrink: 0 }} />
                    }
                    <div>
                      <p className="st-dropzone-filename">{uploadFile.name}</p>
                      <p className="st-dropzone-filesize">{(uploadFile.size / 1024).toFixed(1)} KB</p>
                    </div>
                    <button className="st-dropzone-remove" onClick={(e) => { e.stopPropagation(); setUploadFile(null); setParsedShifts(null); }}>
                      <X size={16} />
                    </button>
                  </div>
                ) : (
                  <div className="st-dropzone-empty">
                    <Upload size={36} style={{ color: 'var(--theme-textSecondary, #94a3b8)', marginBottom: '0.4rem' }} />
                    <p>Drop file here or click to browse</p>
                    <p style={{ fontSize: '0.78rem', color: 'var(--theme-textSecondary)', margin: 0 }}>Supports .xlsx spreadsheets</p>
                  </div>
                )}
              </div>

              {/* Parse Action */}
              {uploadFile && !parsedShifts && (
                <div className="st-upload-actions">
                  <button className="st-btn-primary" onClick={handleParseUpload} disabled={uploading}>
                    {uploading
                      ? <><Loader size={14} className="st-spin" /> Parsing…</>
                      : <><Upload size={14} /> Parse &amp; Preview</>
                    }
                  </button>
                </div>
              )}

              {/* Parsed Preview */}
              {parsedShifts && (
                <div className="st-preview">
                  {/* Summary bar */}
                  <div className="st-preview-summary">
                    <div className="st-preview-stat">
                      <CheckCircle size={14} style={{ color: '#15803d' }} />
                      <span>{parsedShifts.records?.length || 0} shift records parsed</span>
                    </div>
                    {parsedShifts.unmatched?.length > 0 && (
                      <div className="st-preview-stat">
                        <XCircle size={14} style={{ color: '#b91c1c' }} />
                        <span>{parsedShifts.unmatched.length} unmatched</span>
                      </div>
                    )}
                  </div>

                  {parsedShifts.summary && (
                    <div className="st-note-info">{parsedShifts.summary}</div>
                  )}

                  {parsedShifts.unmatched?.length > 0 && (
                    <div className="st-note-warn">
                      <strong>Unmatched employees:</strong> {parsedShifts.unmatched.join(', ')}
                    </div>
                  )}

                  {parsedShifts.records?.length > 0 && (() => {
                    const empMap = {};
                    parsedShifts.records.forEach(rec => {
                      const eid = rec.employee_id || rec.employeeId;
                      if (!empMap[eid]) empMap[eid] = { name: rec.employee_name || rec.employeeName || eid, id: eid, weeks: {} };
                      empMap[eid].weeks[rec.week_number || rec.weekNumber] = {
                        shift: rec.shift_type || rec.shiftType || '',
                        off:   rec.off_day ?? rec.offDay ?? null,
                      };
                    });
                    const empList  = Object.values(empMap);
                    const weekNums = [...new Set(parsedShifts.records.map(r => r.week_number || r.weekNumber))].sort();
                    const wks      = getWeeksInMonth();
                    const offDayNames = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];

                    const toggleShift = (empId, weekNum) => {
                      const cycle = ['morning','day','evening','night','planned-leave'];
                      setParsedShifts(prev => ({
                        ...prev,
                        records: prev.records.map(r => {
                          const rid = r.employee_id || r.employeeId;
                          const rwn = r.week_number || r.weekNumber;
                          if (rid === empId && rwn === weekNum) {
                            const cur  = r.shift_type || r.shiftType || 'day';
                            const next = cycle[(cycle.indexOf(cur) + 1) % cycle.length];
                            return { ...r, shift_type: next, shiftType: next };
                          }
                          return r;
                        })
                      }));
                    };

                    const cycleOffDay = (empId, weekNum) => {
                      setParsedShifts(prev => ({
                        ...prev,
                        records: prev.records.map(r => {
                          const rid = r.employee_id || r.employeeId;
                          const rwn = r.week_number || r.weekNumber;
                          if (rid === empId && rwn === weekNum) {
                            const cur  = r.off_day ?? r.offDay ?? -1;
                            const next = cur === null || cur === -1 ? 0 : (cur + 1) % 7;
                            return { ...r, off_day: next, offDay: next };
                          }
                          return r;
                        })
                      }));
                    };

                    return (
                      <>
                        <p style={{ fontSize: '0.75rem', color: 'var(--theme-textSecondary)', marginBottom: '0.5rem' }}>
                          Click shift to cycle type · Click off day to change
                        </p>

                        <div className="st-grid-table-wrap">
                          <table className="st-grid-table">
                            <thead>
                              <tr>
                                <th className="st-grid-th-employee">Employee</th>
                                {weekNums.map(wn => {
                                  const wk = wks[wn - 1];
                                  return (
                                    <th key={wn} colSpan={2} className="st-grid-th-week">
                                      Week {wn}
                                      {wk && <span className="st-grid-th-week-sub">{formatDateRange(wk.startDate, wk.endDate)}</span>}
                                    </th>
                                  );
                                })}
                              </tr>
                              <tr>
                                <th className="st-grid-th-employee" style={{ fontWeight: 400 }}></th>
                                {weekNums.map(wn => (
                                  <React.Fragment key={wn}>
                                    <th className="st-grid-th-week" style={{ fontSize: '0.68rem' }}>Shift</th>
                                    <th className="st-grid-th-week" style={{ fontSize: '0.68rem' }}>Off Day</th>
                                  </React.Fragment>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {empList.map((emp, idx) => (
                                <tr key={emp.id} style={{ background: idx % 2 === 0 ? 'var(--theme-surface, #fdf9f3)' : 'var(--theme-surfaceHover, #f0ebe0)' }}>
                                  <td className="st-grid-td-employee">
                                    <div className="st-emp-name">{emp.name}</div>
                                    <div className="st-emp-meta">{emp.id}</div>
                                  </td>
                                  {weekNums.map(wn => {
                                    const wd      = emp.weeks[wn];
                                    const sc      = shiftStyle[wd?.shift] || shiftStyle.day;
                                    const offIdx  = wd?.off;
                                    const offLabel = offIdx !== null && offIdx !== undefined && offIdx >= 0 ? offDayNames[offIdx] : '—';
                                    return (
                                      <React.Fragment key={wn}>
                                        <td
                                          className="st-grid-td-cell"
                                          onClick={() => toggleShift(emp.id, wn)}
                                          style={{ textAlign: 'center', cursor: 'pointer' }}
                                          title={`Click to change shift (Week ${wn})`}
                                        >
                                          {wd
                                            ? <span className="st-shift-pill" style={{ background: sc.bg, color: sc.color }}>{sc.label}</span>
                                            : <span style={{ color: 'var(--theme-textSecondary)' }}>—</span>
                                          }
                                        </td>
                                        <td
                                          className="st-grid-td-cell"
                                          onClick={() => cycleOffDay(emp.id, wn)}
                                          style={{ textAlign: 'center', cursor: 'pointer' }}
                                          title={`Click to change off day (Week ${wn})`}
                                        >
                                          <span style={{
                                            display: 'inline-block', padding: '0.2rem 0.5rem', borderRadius: '3px',
                                            fontSize: '0.75rem', fontWeight: 600,
                                            background: offIdx !== null && offIdx >= 0 ? 'var(--theme-surfaceHover, #e5e7eb)' : 'transparent',
                                            color: offIdx !== null && offIdx >= 0 ? 'var(--theme-text, #374151)' : 'var(--theme-textSecondary)',
                                          }}>
                                            {offLabel}
                                          </span>
                                        </td>
                                      </React.Fragment>
                                    );
                                  })}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>

                        {/* Shift legend */}
                        <div className="st-legend-strip">
                          {Object.entries(shiftStyle).map(([key, val]) => (
                            <span key={key} className="st-legend-item">
                              <span className="st-legend-dot" style={{ background: val.bg, border: `1px solid ${val.color}` }} />
                              <span className="st-legend-name">{val.label}</span>
                            </span>
                          ))}
                        </div>
                      </>
                    );
                  })()}

                  <div className="st-upload-actions" style={{ marginTop: '1rem' }}>
                    <button className="st-btn-secondary" onClick={() => { setParsedShifts(null); setUploadFile(null); }}>
                      Cancel
                    </button>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '0.3rem' }}>
                      <button className="st-btn-primary" onClick={handleConfirmUpload} disabled={confirmLoading || !parsedShifts.records?.length}>
                        {confirmLoading
                          ? <><Loader size={14} className="st-spin" /> Saving…</>
                          : <><CheckCircle size={14} /> Submit &amp; Save ({parsedShifts.records?.length} records)</>
                        }
                      </button>
                      <span style={{ fontSize: '0.72rem', color: 'var(--theme-textSecondary)' }}>
                        Overwrites existing shift schedule for this month
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── Grid Tab ────────────────────────────────────────────────── */}
          {activeTab === 'grid' && (
            <div className="st-card">
              {loading.employees && (
                <div className="st-loading">
                  <Loader size={20} className="st-spin" />
                  <p>Loading employees…</p>
                </div>
              )}

              {!loading.employees && getEmployees().length === 0 && (
                <div className="st-empty">
                  <UsersIcon size={32} />
                  <p>No employees found for this organization.</p>
                </div>
              )}

              {!loading.employees && getEmployees().length > 0 && (() => {
                const emps = getEmployees();

                return (
                  <>
                    <div className="st-grid-table-wrap">
                      <table className="st-grid-table">
                        <thead>
                          <tr>
                            <th className="st-grid-th-employee">Employee</th>
                            {weeks.map((wk, wi) => (
                              <th key={wi} className="st-grid-th-week">
                                Week {wk.weekNum}
                                <span className="st-grid-th-week-sub">{formatDateRange(wk.startDate, wk.endDate)}</span>
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {emps.map((emp, idx) => (
                            <tr key={emp.id} style={{ background: idx % 2 === 0 ? 'var(--theme-surface, #fdf9f3)' : 'var(--theme-surfaceHover, #f0ebe0)' }}>
                              <td className="st-grid-td-employee">
                                <div className="st-emp-name">{emp.name}</div>
                                <div className="st-emp-meta">{emp.empId} · {emp.department}</div>
                              </td>
                              {weeks.map((wk, wi) => {
                                const weekKey    = `week${wi + 1}`;
                                const wd         = employeeShifts[emp.id]?.[weekKey];
                                const st         = shiftStyle[wd?.shiftType] || null;
                                const offDayInfo = wd?.offDay !== '' && wd?.offDay !== undefined ? wk.days[wd.offDay] : null;
                                const cellKey    = `${emp.id}-${weekKey}`;
                                const isEditing  = openLeaveDropdown === cellKey;

                                return (
                                  <td key={wi} className="st-grid-td-cell">
                                    {/* Clickable cell content */}
                                    <div
                                      onClick={() => setOpenLeaveDropdown(isEditing ? null : cellKey)}
                                      style={{ cursor: 'pointer', minHeight: '52px', display: 'flex', flexDirection: 'column', gap: '0.3rem', alignItems: 'flex-start', justifyContent: 'center' }}
                                      title="Click to edit"
                                    >
                                      {st ? (
                                        <>
                                          <span className="st-shift-pill" style={{ background: st.bg, color: st.color }}>
                                            {st.label}
                                            <span className="st-shift-pill-sub">{st.sub}</span>
                                          </span>
                                          {offDayInfo && (
                                            <span className="st-off-day-text">
                                              Off: <strong>{offDayInfo.dayName.slice(0, 3)}, {offDayInfo.dayOfMonth}</strong>
                                            </span>
                                          )}
                                        </>
                                      ) : (
                                        <span className="st-cell-unset">Not set</span>
                                      )}
                                    </div>

                                    {/* Inline edit popover */}
                                    {isEditing && (
                                      <div
                                        data-shift-popover
                                        className={`st-cell-popover ${emps.length > 3 && idx >= emps.length - 3 ? 'st-cell-popover-up' : ''}`}
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        <div className="st-cell-popover-title">
                                          {emp.name} — Week {wi + 1}
                                        </div>

                                        {/* Shift type pills */}
                                        <div className="st-popover-pills">
                                          {shiftTypes.filter(s => s.value).map(s => {
                                            const ss     = shiftStyle[s.value];
                                            const active = wd?.shiftType === s.value;
                                            return (
                                              <button
                                                key={s.value}
                                                onClick={() => handleShiftChange(emp.id, weekKey, s.value)}
                                                className="st-popover-pill"
                                                style={active
                                                  ? { background: ss?.bg, color: ss?.color, borderColor: ss?.color }
                                                  : {}
                                                }
                                              >
                                                {ss?.label || s.label}
                                              </button>
                                            );
                                          })}
                                        </div>

                                        {/* Off day selector */}
                                        {wd?.shiftType && wd.shiftType !== 'planned-leave' && (
                                          <div>
                                            <div className="st-popover-off-label">Off Day</div>
                                            <div className="st-popover-days">
                                              {wk.days.map(d => {
                                                const active = wd?.offDay !== '' && wd?.offDay !== undefined && parseInt(wd.offDay) === d.value;
                                                return (
                                                  <button
                                                    key={d.value}
                                                    onClick={() => handleOffDayChange(emp.id, weekKey, active ? '' : d.value)}
                                                    className="st-popover-day"
                                                    style={active
                                                      ? { background: 'var(--theme-primary, #2563eb)', color: '#fff', borderColor: 'var(--theme-primary, #2563eb)' }
                                                      : {}
                                                    }
                                                  >
                                                    {d.dayName.slice(0, 3)}<br />
                                                    <span style={{ fontSize: '0.63rem', fontWeight: 400 }}>{d.dayOfMonth}</span>
                                                  </button>
                                                );
                                              })}
                                            </div>
                                          </div>
                                        )}

                                        <button
                                          onClick={() => setOpenLeaveDropdown(null)}
                                          className="st-popover-done"
                                        >
                                          Done
                                        </button>
                                      </div>
                                    )}
                                  </td>
                                );
                              })}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Legend */}
                    <div className="st-legend-strip">
                      {Object.entries(shiftStyle).map(([key, val]) => (
                        <span key={key} className="st-legend-item">
                          <span className="st-legend-dot" style={{ background: val.bg, border: `1px solid ${val.color}` }} />
                          <span className="st-legend-name">{val.label}</span>
                          <span className="st-legend-hours">{val.sub}</span>
                        </span>
                      ))}
                    </div>

                    {/* Actions */}
                    <div className="st-grid-actions">
                      <button className="st-btn-secondary" onClick={handleSaveToExcel}>
                        <Download size={13} /> Export Excel
                      </button>
                      <button className="st-btn-primary" onClick={handleSubmit} disabled={isSubmitting}>
                        {isSubmitting
                          ? <><Loader size={13} className="st-spin" /> Saving…</>
                          : <><Save size={13} /> Save Shifts</>
                        }
                      </button>
                    </div>
                  </>
                );
              })()}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ShiftTracker;
